import React, { useState, useEffect } from "react";
import {
    Col,
    Tooltip,
    Typography,
} from "antd";
import TableSearchBar from "./tempComponents/searchBar";
import ConnectedAccountsRowDropdown from "./tempComponents/connectedAccounts/rowDropdown";
import ConnectedAccountsTableDropdown from "./tempComponents/connectedAccounts/tableDropdown";
import { amountStrtoFloat, toPrice } from "../../../helpers/stringFormat";
import TableWithLoadingWrapper from "../../../components/generic/dataGrid";
import { generateBankAccountDetailsUrl } from "../../../components/bankAccount/generateBankAccountDetailsUrl";
import GenericTagChips from "../../../components/specific/customTags/genericTagChips";
import GenericLink from "../../../components/generic/genericLink";
import { Stack, Grid } from "@mui/material";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";
import PermissionGate from '../../../helpers/PermissionGate'
import SelectBankAccountTag from "../../../components/specific/customTags/selectBankAccountTag";
import { Select, MenuItem, FormControl, Button } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import OutlinedInput from '@mui/material/OutlinedInput';
import { styled } from '@mui/material/styles'
import SelectBankAccountPurpose from "../../../components/specific/customTags/selectBankAccountPurpose";
import ExportConnectedAccountModal from "./tempComponents/connectedAccounts/tableDropdown/ExportConnectedAccountModal";

const connectedAccountDataFormat = {
    id: "",
    description: "",
    account: {
        source: "",
        account_type: "",
        available_balance: "$300,000.00",
        current_balance: "$300,000.00",
        aggregated_balance: "$300,000.00",
        account_source: "",
        bank: {
            id: "",
            name: "",
            address: "",
        },
        account_description: "",
        status: "",
        owner_info: { owner_name: "", owner_id: "", owner_attribute: "" },
        api_connection_status_live: true,
        api_last_connection_time: "",
        has_statements: true,
        source_account_id: "",
        routing_number: "",
    },
    beneficiary: { id: "", name: "", address: "" },
    key: "",
    property: "",
    portfolio: "",
    propertyId: "",
    portfolioId: "",
    type: "",
    purpose: {
        id: "",
        name: "",
        description: "",
    }
};


const ConnectedAccounts = (props) => {
    const [connectedAccounts, setConnectedAccounts] = useState(props.connectedAccounts || []);
    const [showAccounts, setShowAccounts] = useState([]);
    const [balance, setBalance] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedInstitutions, setSelectedInstitutions] = useState([]);
    const [institutionOptions, setInstitutionOptions] = useState([]);
    const [selectedTagIds, setSelectedTagIds] = useState([]);
    const [isFilterActive, setIsFilterActive] = useState(false);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectPurposeId, setSelectPurposeId] = useState(undefined);
    const [csvModalVisible, setCsvModalVisible] = useState(false);

    const CustomSelect = styled(Select)({
        '& .MuiSelect-icon': {
            fontSize: '13px',
            rotate: '-90deg',
            color: 'lightgrey',
            top: '22%',
            transform: 'translateY(-50%)',
        },
    });

    useEffect(() => {
        const accounts = props.connectedAccounts.map((account) => {
            return {
                ...account,
                key: account.id,
            };
        });

        setConnectedAccounts(accounts);

        if (!isFilterActive) {
            setShowAccounts(accounts);
            // setSearchResults([]);
        }
    }, [props.connectedAccounts, isFilterActive]);

    useEffect(() => {
        if (searchText.length > 0) {
            const loweredSearch = searchText.toLowerCase();

            const results = showAccounts && showAccounts.filter((row) => {
                // Define searchable fields (customize as needed)
                const bankName = row?.account?.bank?.name || "";
                const description = row?.description || "";
                const beneficiaryName = row?.beneficiary?.name || "";
                const portfolio = row?.portfolio || "";
                const property = row?.property || "";
                const purpose = row?.purpose?.name || "";
                return (
                    bankName.toLowerCase().includes(loweredSearch) ||
                    description.toLowerCase().includes(loweredSearch) ||
                    beneficiaryName.toLowerCase().includes(loweredSearch) ||
                    portfolio.toLowerCase().includes(loweredSearch) ||
                    property.toLowerCase().includes(loweredSearch) ||
                    purpose.toLowerCase().includes(loweredSearch)
                );
            });

            setSearchResults(results);
            setIsFilterActive(true);
        } else if (filteredAccounts.length) {
            setFilteredAccounts(filteredAccounts);
            setIsFilterActive(true);
        }
        else {
            setSearchResults([]);
            setIsFilterActive(false);
        }
    }, [searchText, showAccounts, filteredAccounts]);


    useEffect(() => {
        if (searchText && searchResults.length > 0) {
            balanceHelper(searchResults); // Search active (with or without filters)
        } else if (isFilterActive) {
            balanceHelper(filteredAccounts); // Filters only
        } else {
            balanceHelper(showAccounts); // No filter or search
        }
    }, [showAccounts, isFilterActive, filteredAccounts, searchResults, searchText]);



    function balanceHelper(accounts) {
        const balance = accounts.reduce((runningSum, row) => {
            let amount = amountStrtoFloat(row.account.aggregated_balance)
            return runningSum + amount
        }, 0);
        setBalance(balance)
    }

    const handleRemove = (account) => {
        const newSelectedRows = selectedRows.filter((row) => row.key !== account.key);
        setSelectedRows(newSelectedRows);
    };

    useEffect(() => {
        const uniqueInstitutions = [
            ...new Set(connectedAccounts.map(acc => acc?.account?.bank?.name).filter(Boolean))
        ];
        setInstitutionOptions(uniqueInstitutions);
    }, [connectedAccounts]);

    const handleApplyFilters = () => {

        let filtered = [...props.connectedAccounts];
        const hasInstitutionFilter = selectedInstitutions.length > 0;
        const hasTagFilter = selectedTagIds.length > 0;
        const hasPurposeFilter = !!selectPurposeId;

        if (hasInstitutionFilter) {
            filtered = filtered.filter((connection) =>
                selectedInstitutions.includes(connection.account.bank.name)
            );
        }

        if (hasTagFilter) {
            const selectedGroupNumbers = selectedTagIds.map(id => parseInt(id.split('_')[1]));
            filtered = filtered.filter((connection) =>
                connection.generic_tags?.some((tag) =>
                    selectedGroupNumbers.includes(tag?.id)
                )
            );
        }

        if (hasPurposeFilter) {
            filtered = filtered.filter((connection) =>
                connection.purpose?.id === parseInt(selectPurposeId)
            );
        }

        const isFiltering = hasInstitutionFilter || hasTagFilter || hasPurposeFilter;
        setIsFilterActive(isFiltering);
        setFilteredAccounts(filtered);
    };

    const handleClearFilters = () => {
        setSelectedInstitutions([]);
        setSelectedTagIds([]);
        setFilteredAccounts([]);
        setSelectPurposeId(undefined);
        setShowAccounts(connectedAccounts)
        setIsFilterActive(false);
    };

    const columns = [
        {
            field: 'bank_name',
            headerName: 'Institution',
            valueGetter: (_, params) => {
                return params?.account?.bank?.name;
            },
            renderCell: (params) => (
                <div style={{ width: '100%' }}>
                    {params?.row?.account?.bank?.name}
                </div>
            ),
        },
        {
            field: 'description',
            headerName: 'Account',
            flex: 0.6,
            renderCell: (params) => {
                const { value, row } = params;
                const ownerInfo = row?.account?.owner_info;
                const url = generateBankAccountDetailsUrl(
                    ownerInfo?.owner_name,
                    row?.id,
                    ownerInfo?.owner_id,
                    row?.purpose?.name,
                );
                const last4 = row?.account?.account_number_last_4;
                const isValidLast4 = last4 !== undefined && last4?.length > 3 && last4?.length < 7;
                if (!value) return;
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <GenericLink
                            toURL={url}
                            onClick={() => { }}
                            text={value}
                        />
                        {isValidLast4 && (
                            <Typography variant="body2" color="textDisabled" style={{ fontStyle: 'italic' }}>
                                ....{last4}
                            </Typography>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'beneficiary_name',
            headerName: 'Holding Entity',
            valueGetter: (_, params) => {
                return params?.beneficiary?.name;
            },
            renderCell: (params) => {
                const { value, row } = params;
                const url = row?.beneficiary?.id !== "N/A" ? `/holding-entities/details/000${row?.beneficiary?.id}` : "";
                return url !== "" ? (

                    <GenericLink
                        toURL={url}
                        onClick={() => { }}
                        text={value}
                    />
                ) : (
                    value && value?.toLowerCase() !== "unknown" ? (
                        <span>{value}</span>
                    ) : (
                        <Typography variant="body2" color="textDisabled" style={{ fontStyle: 'italic' }}>
                            N/A
                        </Typography>
                    )
                );
            },
        },
        {
            field: 'placement',
            headerName: 'Placement',
            flex: 1.5,
            renderCell: (params) => {
                return <GenericTagChips genericTags={params?.row?.generic_tags} purpose={params?.row?.purpose} />
            }
        },
        {
            field: 'aggregated_balance',
            headerName: 'Balance',
            valueGetter: (_, params) => {
                return amountStrtoFloat(params?.account?.aggregated_balance);
            },
            renderCell: (params) => {
                return toPrice(params?.value);
            },
            sortComparator: (v1, v2) => {
                return v1 - v2;
            },
        },
        {
            field: 'api_connection_status_live',
            headerName: 'Status',
            valueGetter: (_, params) => {
                return params?.account?.api_connection_status_live;
            },
            renderCell: (params) => (
                <>
                    {params?.value ? (
                        <span>Live</span>
                    ) : (
                        <Tooltip title={"Last live at " + params?.row?.account?.api_last_connection_time}>
                            <span>Not Live</span>
                        </Tooltip>
                    )}
                </>
            ),
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.1,
            align: 'right',
            renderCell: (params) => (
                <PermissionGate requiredRole='dashboardEditor'>
                    <ConnectedAccountsRowDropdown
                        hasStatements={params?.row?.account?.has_statements}
                        setClosedAccounts={props.setClosedAccounts}
                        setConnectedAccounts={props.setConnectedAccounts}
                        markedClosed={params?.row?.account?.marked_closed}
                        hasVerificationDocument={params?.row?.account?.source === "UN"}
                        isClosable={params?.row?.account?.source === "UN"}
                        hasACHDetailsDocument={params?.row?.account?.source === "UN"}
                        editBankAccount={props.editBankAccount}
                        reassignBankAccount={props.reassignBankAccount}
                        deleteBankAccount={props.deleteBankAccount}
                        record={params?.row}
                    />
                </PermissionGate>
            ),
        },
    ];

    return (
        <Stack direction="column" spacing={1}>
            {/* Multi Filter Section */}
            <Grid container columns={16} direction="row" wrap="nowrap"
                sx={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: '8px',
                    flexWrap: 'nowrap'
                }}>
                {/* Multi-Select Filter */}
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                        <CustomSelect
                            multiple
                            displayEmpty
                            IconComponent={ArrowBackIosIcon}
                            input={
                                <OutlinedInput
                                    sx={{
                                        height: '32px',
                                        backgroundColor: 'white',
                                        // marginRight: '8px',
                                        borderRadius: '2px',
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: selectedInstitutions.length === 0 ? 'rgb(211, 211, 211, 0.6)' : '#3498db',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: selectedInstitutions.length === 0 ? 'rgb(211, 211, 211, 0.6)' : '#3498db',
                                        },
                                    }}
                                />
                            }
                            value={selectedInstitutions}
                            onChange={(e) => setSelectedInstitutions(e.target.value)}
                            renderValue={(selected) => {
                                if (selected.length === 0) {
                                    return <span style={{ color: '#9e9e9e', opacity: '50%' }}>Institutions Filter</span>;
                                }
                                return selected.join(', ');
                            }}
                        >
                            {[...institutionOptions]
                                .sort((a, b) => a.localeCompare(b))
                                .map((institution) => (
                                    <MenuItem key={institution} value={institution}>
                                        {institution}
                                    </MenuItem>
                                ))}
                        </CustomSelect>
                    </FormControl>
                </Grid>

                {/* Bank Account Search */}
                <Grid item xs={12} sm={4} >
                    <SelectBankAccountTag
                        loadInComponent={true}
                        lazyLoad={true}
                        loading={false}
                        multiSelect={true}
                        placeholder={"Hierarchy Tag Filter"}
                        value={selectedTagIds}
                        onChange={(selectedIds) => {
                            setSelectedTagIds(selectedIds);
                        }}
                        includeUncategorized={false}
                        required={true}
                    />
                </Grid>

                <Grid item xs={12} sm={4} >
                    <SelectBankAccountPurpose
                        placeholder={"Select purpose of account"}
                        onChange={(e) => {
                            setSelectPurposeId(e);
                        }}
                        currentPurposeId={selectPurposeId}
                    />
                </Grid>

                {/* Apply and Clear Buttons */}
                <Grid item xs={12} sm={4}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button sx={{ fontSize: '12px' }} size="medium" variant="contained" onClick={handleApplyFilters}>
                            Apply
                        </Button>
                        <Button sx={{ fontSize: '12px' }} size="medium" variant="outlined" onClick={handleClearFilters}>
                            Clear
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            <Grid container alignItems="flex-end">
                <Grid item xs={6}>
                    <TableSearchBar
                        originalTableData={isFilterActive ? filteredAccounts : showAccounts}
                        updateTableState={setSearchResults}
                        setIsFilterActive={setIsFilterActive}
                        setSearchText={setSearchText}
                        searchKeys={[["account", "bank", "name"], ["description"], ["beneficiary", "name"], ["portfolio"], ["property"], ["purpose", "name"]]}
                    // isLoading={props.loadingConnectedAccounts}
                    />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
                    <Button variant="contained" disabled={(!searchResults || !filteredAccounts || !showAccounts || showAccounts.length === 0)}
                        onClick={(!searchResults || !filteredAccounts || !showAccounts || showAccounts.length === 0) ? undefined : () => setCsvModalVisible(true)} sx={{ fontSize: '12px', boxShadow: 'none' }}>
                        Export Data
                    </Button>

                    <ConnectedAccountsTableDropdown
                        selectedRows={selectedRows}
                        tableData={searchResults.length > 0 || searchText ? searchResults : (isFilterActive ? filteredAccounts : showAccounts)}
                        entities={props.entities}
                        bankAccounts={[]}
                        handleRemove={handleRemove}
                        subPortfolios={props.subPortfolios}
                        unlinkedProperties={props.unlinkedProperties}
                        assignEntityToBankAccounts={props.assignEntityToBankAccounts}
                        unassignEntityFromBankAccounts={props.unassignEntityFromBankAccounts}
                    />
                    <ExportConnectedAccountModal
                        tableData={searchResults.length > 0 || searchText ? searchResults : (isFilterActive ? filteredAccounts : showAccounts)}
                        csvModalVisible={csvModalVisible}
                        handleCancelModal={() => setCsvModalVisible(false)}
                    />
                </Grid>
            </Grid>

            <TableWithLoadingWrapper
                columns={columns}
                dataSource={searchResults.length > 0 || searchText ? searchResults : (isFilterActive ? filteredAccounts : showAccounts)}
                optInRowHeightAuto={true}
                pagination={{
                    paginationModel: { pageSize: showAccounts?.length || 100, page: 0 },
                }}
                pageSizeOptions={[10, 20, 30, 50, 100]}
                onChange={(pagination, filters, sorter, extra) => {
                    const filteredData = extra.currentDataSource;
                    const totalBalance = filteredData.reduce((sum, record) => sum + amountStrtoFloat(record?.account?.aggregated_balance), 0);
                    setBalance(totalBalance);
                }}
                numerator={props.numerator}
                denominator={props.denominator}
                sortModel={[
                    {
                        field: "bank_name",
                        sort: "asc",
                    },
                ]}
                onRowSelectionModelChange={(newSelectionIds) => {
                    const newSelectionAccounts = showAccounts.filter((row) => newSelectionIds?.includes(row.id));
                    const selectedAccounts = newSelectionAccounts.map((row) => {
                        return {
                            account: row?.description,
                            bankId: row?.id,
                            key: row?.key,
                            source: row?.account?.source,
                            hasStatements: row?.account?.has_statements,
                            live: row?.account?.api_connection_status_live,
                            entityId: row?.beneficiary?.id,
                            entityName: row?.beneficiary?.name,
                            institution: row?.account?.bank_name,
                            last4: row?.account?.account_number_last_4,
                        };
                    });
                    setSelectedRows(selectedAccounts);
                }}
                // getRowHeight={() => 'auto'}
                selectionModel={selectedRows}
                dataFormat={connectedAccountDataFormat}
                numLoadingEntries={3}
                loading={props.loadingConnectedAccounts}
                loadingEntriesTop={true}
                checkboxSelection={true}
                getRowId={(row) => row?.id}
            />
        </Stack>
    );
};

export default ConnectedAccounts
